/**
 * FreeShippingProgress — cart drawer free-shipping goal with live progress.
 *
 * Threshold and total-price are both in cents (same as Shopify cart line prices).
 * Listens to cart:updated for live updates between section re-renders.
 *
 * Placeholders in message HTML:
 * - [[amount]] or {{amount}} — remaining amount before the goal
 * - [[goal]] or {{goal}} — goal threshold
 */

if (!customElements.get('free-shipping-progress')) {
  class FreeShippingProgress extends HTMLElement {
    static get observedAttributes() {
      return ['threshold', 'total-price'];
    }

    constructor() {
      super();
      this._onCartChanged = this._onCartChanged.bind(this);
    }

    connectedCallback() {
      const locale = window.Shopify?.locale || document.documentElement.lang || 'en';
      const currency = window.Shopify?.currency?.active || window.themeVariables?.settings?.cartCurrency || 'USD';

      this._formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency
      });

      document.documentElement.addEventListener('cart:updated', this._onCartChanged);
      this._ensureRefs();
      this._render();
    }

    disconnectedCallback() {
      document.documentElement.removeEventListener('cart:updated', this._onCartChanged);
    }

    get totalPriceCents() {
      return Number.parseInt(this.getAttribute('total-price') || '0', 10) || 0;
    }

    set totalPriceCents(value) {
      this.setAttribute('total-price', String(value));
    }

    get thresholdCents() {
      return Number.parseInt(this.getAttribute('threshold') || '0', 10) || 0;
    }

    attributeChangedCallback() {
      if (this.isConnected) {
        this._render();
      }
    }

    _shippingSubtotalFromCart(cart) {
      const items = Array.isArray(cart?.items) ? cart.items : [];
      const apps = Array.isArray(cart?.cart_level_discount_applications)
        ? cart.cart_level_discount_applications
        : [];

      const shippingLines = items.filter((item) => item.requires_shipping);
      const lineSum = shippingLines.reduce((sum, item) => sum + (item.final_line_price || 0), 0);
      const cartDiscount = apps.reduce((sum, d) => sum + (d.total_allocated_amount || 0), 0);
      return Math.max(0, lineSum - cartDiscount);
    }

    _onCartChanged(event) {
      const cart = event.detail?.cart;
      if (!cart) return;
      this.totalPriceCents = this._shippingSubtotalFromCart(cart);
    }

    _ensureRefs() {
      this._messageEl = this.querySelector('[data-fsp-message]');
      this._fillEl = this.querySelector('[data-fsp-fill]');
      this._thumbEl = this.querySelector('[data-fsp-thumb]');
      this._trackEl = this.querySelector('[data-fsp-track]');
    }

    _formatMoneyFromCents(cents) {
      return this._formatter.format(cents / 100);
    }

    _escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    _applyPlaceholders(template, { amount, goal }) {
      return template
        .replace(/\{\{\s*amount\s*\}\}|\[\[amount\]\]/gi, this._escapeHtml(amount))
        .replace(/\{\{\s*goal\s*\}\}|\[\[goal\]\]/gi, this._escapeHtml(goal));
    }

    _render() {
      if (!this._messageEl || !this._fillEl || !this._thumbEl || !this._trackEl) {
        this._ensureRefs();
      }

      if (!this._messageEl || !this._fillEl || !this._thumbEl || !this._trackEl) {
        return;
      }

      const threshold = this.thresholdCents;
      const total = this.totalPriceCents;
      const goalFormatted = this._formatMoneyFromCents(threshold);

      let pct = 0;
      if (threshold > 0) {
        pct = Math.min(100, Math.max(0, (total / threshold) * 100));
      } else {
        pct = 100;
      }

      const beforeTpl = this.getAttribute('data-message-before') || '';
      const afterTpl = this.getAttribute('data-message-after') || '';
      const reached = threshold > 0 && total >= threshold;

      if (reached) {
        this._messageEl.innerHTML = this._applyPlaceholders(afterTpl, {
          amount: this._formatMoneyFromCents(0),
          goal: goalFormatted
        });
      } else {
        const remainingCents = Math.max(0, threshold - total);
        this._messageEl.innerHTML = this._applyPlaceholders(beforeTpl, {
          amount: this._formatMoneyFromCents(remainingCents),
          goal: goalFormatted
        });
      }

      this._fillEl.style.width = `${pct}%`;
      this._thumbEl.style.left = `${pct}%`;
      this.style.setProperty('--fsp-pct', `${pct}%`);

      this._trackEl.setAttribute('aria-valuemin', '0');
      this._trackEl.setAttribute('aria-valuemax', String(threshold));
      this._trackEl.setAttribute('aria-valuenow', String(Math.min(total, threshold)));
    }
  }

  window.customElements.define('free-shipping-progress', FreeShippingProgress);
}
